import { Avatar, Table, Space, Tag, Card } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getImageUrl, getDataDetail } from 'utils/tools';
import lodashValues from 'lodash/values';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { omit, flatten } from 'lodash';
import { DynamicObjectType } from 'types/commons';
import { formatMoney } from 'utils/formattedString';

interface IProductsOrderProps {
  data: DynamicObjectType;
  items: DynamicObjectType[];
  title?: string;
  isExchange?: boolean;
  order: DynamicObjectType;
}

function ProductsOrder({
  data: orderData,
  items,
  title,
  isExchange,
  order,
}: IProductsOrderProps) {
  const { t } = useTranslation();
  const stores = useSelector<DynamicObjectType>(
    (state) => state.config.stores,
  ) as DynamicObjectType[];

  const products = useMemo(() => {
    return flatten(
      items?.map((e, itemIndex) => {
        const stock = e?.stocks?.reduce(
          (e, key) => ({
            ...e,
            [key.id]: {
              ...key,
              ...(orderData?.orderType === 'TAB_TRY_BUY'
                ? orderData?.location
                : stores?.find((item) => item.id === key.id)),
            },
          }),
          {},
        );

        const variantOriginalOrder = order?.originalOrder?.items?.find(
          (i) => i?.variantId === e?.variantId,
        );

        return [
          {
            ...e,
            productSortingIndex: itemIndex,
            name: e.productName || e?.name,
            properties: ['white', 'size-4K'],
            quantity: e.quantity,
            price: Number(e.price),
            currency: 'đ',
            thumbnail:
              e?.variant?.thumbnail ||
              e.productThumbnail ||
              e?.propduct?.thumbnail,
            stocks: lodashValues(stock),
            itemIndex,
            isMatchLotDate: e?.variant?.isMatchLotDate,
            ...(e?.product?.comboItems?.length > 0 && {
              isCombo: true,
            }),
            // variant:
            //   orderData?.items?.find(
            //     (i) =>
            //       i?.variantSku === e?.variantSku ||
            //       i?.variantSku === e?.variant?.sku,
            //   )?.variant || {},
            variantAttributeText:
              e?.variantText ||
              variantOriginalOrder?.variantText ||
              variantOriginalOrder?.variant?.attributeValues
                ?.map((item) => getDataDetail(item)?.name)
                ?.join('/'),
          },
          ...(e?.product?.comboItems?.length > 0
            ? e?.product?.comboItems?.map((comboItem, comboIndex) => ({
                ...comboItem?.product,
                ...comboItem,
                isMatchLotDate: comboItem?.variant?.isMatchLotDate,
                comboIndex,
                itemIndex,
                quantity: +comboItem?.quantity * +e?.quantity,
                discountedPrice: +comboItem.price / (+comboItem?.quantity || 1),
                comboProduct: {
                  ...omit(e, ['comboItems']),
                  productSortingIndex: itemIndex,
                },
                isComboItem: true,
                name: comboItem.productName,
                currency: 'đ',
                thumbnail:
                  comboItem?.variant?.thumbnail ||
                  comboItem.productThumbnail ||
                  comboItem?.propduct?.thumbnail,
                stocks: lodashValues(
                  comboItem?.stocks?.reduce(
                    (obj, key) => ({
                      ...obj,
                      [key.id]: {
                        ...key,
                        ...(orderData?.orderType === 'TAB_TRY_BUY'
                          ? orderData?.location
                          : stores?.find((item) => item.id === key.id)),
                      },
                    }),
                    {},
                  ),
                ),
              })) || []
            : []),
        ];
      }) || [],
    );
  }, [orderData, stores, items]);

  const columns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'id',
        render: (data, record) => {
          return (
            <div
              className={`d-flex ${record?.isComboItem && 'ml-[40px]'} items-center`}
            >
              <Avatar
                size="large"
                shape="square"
                style={{ width: 40, height: 40, marginRight: 20 }}
                src={getImageUrl(record?.thumbnail)}
              />
              <div>
                <Space size={4}>
                  {record?.isGift && <Tag color="red">Gift</Tag>}
                  <div>{`${record?.isCombo ? 'Combo: ' : ''}${
                    getDataDetail(
                      record,
                      'detail',
                      'name',
                      record?.name ||
                        record?.productName ||
                        record?.variant?.slug,
                    )?.name
                  }`}</div>
                  {record?.isComboItem && (
                    <Tag
                      color="geekblue"
                      style={{ whiteSpace: 'break-spaces' }}
                    >
                      {`Combo: ${getDataDetail(record?.comboProduct)?.name || getDataDetail(record?.comboProduct?.product)?.name}`}
                    </Tag>
                  )}
                  {record?.bundle && (
                    <Tag
                      color="geekblue"
                      style={{ whiteSpace: 'break-spaces' }}
                    >
                      {`Bundle: ${getDataDetail(record?.bundle)?.name}`}
                    </Tag>
                  )}
                </Space>
                {!record?.variantAttributeText ? (
                  <div className="text-secondary text-sm">
                    {record?.variant?.attributeValues?.length > 0 &&
                      record?.variant?.attributeValues
                        ?.map((item) => getDataDetail(item)?.name)
                        ?.join('/')}
                  </div>
                ) : (
                  <div className="text-secondary text-sm">
                    {record?.variantAttributeText}
                  </div>
                )}
                <div className="description">{`SKU: ${
                  record?.variantSku || record?.variant?.sku
                }`}</div>
              </div>
            </div>
          );
        },
      },
      {
        dataIndex: 'quantity',
        render: (data) => <div className="">{data}</div>,
        title: <div>{t('orderDetail.quantity')}</div>,
      },
      {
        title: t('ordersExchange.orderProduct.finalPrice'),
        dataIndex: 'total',
        key: 'total',
        width: 130,
        render: (text: string, record: DynamicObjectType) => {
          return (
            <div>
              {formatMoney(
                (record?.erpAmount || record?.discountPrice) * record?.quantity,
                0,
                3,
              )}
            </div>
          );
        },
      },
    ],
    [t, orderData],
  );

  return (
    <Wrapper className="w-100">
      <CardStyle title={title}>
        <div className="order-info-box__content">
          <Table
            pagination={false}
            dataSource={products}
            columns={columns}
            rowClassName={(record) => record?.isComboItem && 'combo-item-row'}
          />
        </div>
        <Space size={5} className="w-full p-[10px]" direction="vertical">
          {!isExchange ? (
            <>
              <div className="flex justify-between items-center">
                <div>Total items</div>
                <div>{products?.length || 0}</div>
              </div>
              <div className="flex justify-between items-center">
                <div>Total return</div>
                <div>
                  {formatMoney(Number(order?.returnItemsValue), 0, 3) || 0}
                </div>
              </div>
            </>
          ) : (
            <div className="flex justify-between items-center">
              <div>Total amount</div>
              <div>
                {formatMoney(Number(order?.newOrderItemsValue), 0, 3) || 0}
              </div>
            </div>
          )}
        </Space>
      </CardStyle>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .combo-item-row {
    td {
      background: #red !important;
    }
  }
  .ant-avatar {
    min-width: 40px;
  }
`;

const CardStyle = styled(Card)`
  .ant-card-head {
    padding-left: 16px;
    padding-right: 16px;
  }
  .ant-card-body {
    padding: 0;
  }
`;

ProductsOrder.propTypes = {
  data: PropTypes.object,
};

export default ProductsOrder;
